import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ChartPie } from "lucide-react";
import type { ChartConfig } from "@/components/ui/chart";
import type { DateRange } from "@/hooks/useDateRange";
import { cn } from "@/lib/utils";
import { AnalyticsDonutChartArea } from "../shared/AnalyticsDonutChartArea";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import {
  SERVICE_USAGE_CARD_BORDER,
  SERVICE_USAGE_CARD_GAP,
  SERVICE_USAGE_CARD_HEIGHT,
  SERVICE_USAGE_CARD_PADDING_BOTTOM,
  SERVICE_USAGE_CARD_RADIUS,
  SERVICE_USAGE_CARD_WIDTH,
  SERVICES_CHART_CARD_ICON_COLOR,
} from "../services/constants";
import type { OperationsCategorySegment } from "./types";

interface OperationsTopCategoriesDonutCardProps {
  segments: OperationsCategorySegment[];
  total: number;
  dateRange: DateRange;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
  className?: string;
}

function buildChartConfig(segments: OperationsCategorySegment[]): ChartConfig {
  return segments.reduce<ChartConfig>((config, segment) => {
    config[segment.key] = {
      label: segment.name,
      color: segment.fill,
    };
    return config;
  }, {});
}

function formatDateRangeLabel(dateRange: DateRange): string {
  if (dateRange.from && dateRange.to) {
    return `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`;
  }

  return "April 1, 2026 - April 30, 2026";
}

function formatSegmentPercent(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

function OperationsCategoryTooltip({
  active,
  payload,
  total,
  dateRangeLabel,
}: {
  active?: boolean;
  payload?: Array<{
    value?: number;
    payload?: OperationsCategorySegment;
  }>;
  total: number;
  dateRangeLabel: string;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0];
  const segment = item.payload;
  const value = Number(item.value ?? 0);
  const percent = formatSegmentPercent(value, total);

  return (
    <div className="grid min-w-[11rem] gap-2 rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
      <p className="text-muted-foreground">{dateRangeLabel}</p>
      <div className="flex items-center gap-2">
        <span
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: segment?.fill }}
        />
        <span className="font-medium text-[#171717]">
          {segment?.name} {percent}% ({value})
        </span>
      </div>
    </div>
  );
}

export const OperationsTopCategoriesDonutCard = memo(
  function OperationsTopCategoriesDonutCard({
    segments,
    total,
    dateRange,
    onExport,
    exportDisabled = false,
    className,
  }: OperationsTopCategoriesDonutCardProps) {
    const { t } = useTranslation();
    const chartConfig = useMemo(() => buildChartConfig(segments), [segments]);
    const dateRangeLabel = useMemo(
      () => formatDateRangeLabel(dateRange),
      [dateRange],
    );
    const totalRequestsLabel = t(
      "analytics.operations.totalRequests",
      "Total requests",
    );

    const pieAnimationKey = useMemo(
      () =>
        segments.map((segment) => `${segment.key}:${segment.value}`).join("|"),
      [segments],
    );

    const renderTooltip = useMemo(() => {
      function OperationsCategoryChartTooltip(
        tooltipProps: Record<string, unknown>,
      ) {
        return (
          <OperationsCategoryTooltip
            {...tooltipProps}
            total={total}
            dateRangeLabel={dateRangeLabel}
          />
        );
      }

      return OperationsCategoryChartTooltip;
    }, [dateRangeLabel, total]);

    return (
      <RevenueChartCard
        title={t(
          "analytics.operations.topCategories",
          "TOP 3 categories of requests",
        )}
        width="full"
        cardWidth={SERVICE_USAGE_CARD_WIDTH}
        icon={<ChartPie />}
        iconColor={SERVICES_CHART_CARD_ICON_COLOR}
        onExport={onExport}
        exportDisabled={exportDisabled}
        className={cn("shrink-0 !gap-3", className)}
        style={{
          height: SERVICE_USAGE_CARD_HEIGHT,
          minHeight: SERVICE_USAGE_CARD_HEIGHT,
          borderRadius: SERVICE_USAGE_CARD_RADIUS,
          border: `1px solid ${SERVICE_USAGE_CARD_BORDER}`,
          paddingBottom: SERVICE_USAGE_CARD_PADDING_BOTTOM,
        }}
      >
        <div
          className="flex min-h-0 flex-1 flex-col items-center"
          style={{ gap: SERVICE_USAGE_CARD_GAP }}
        >
          <AnalyticsDonutChartArea
            segments={segments}
            total={total}
            chartConfig={chartConfig}
            centerPrimary={total}
            centerSecondary={totalRequestsLabel}
            pieAnimationKey={pieAnimationKey}
            animate
            tooltip={renderTooltip}
          />

          <ul
            className="flex w-full min-w-0 flex-col"
            style={{ gap: SERVICE_USAGE_CARD_GAP }}
          >
            {segments.map((segment) => {
              const percent = formatSegmentPercent(segment.value, total);

              return (
                <li
                  key={segment.key}
                  className="flex items-center justify-between gap-4"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: segment.fill }}
                    />
                    <span className="truncate text-sm leading-normal text-[#171717]">
                      {segment.name}
                    </span>
                  </div>
                  <span className="shrink-0 text-sm text-[#737373]">
                    {percent}% ({segment.value})
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </RevenueChartCard>
    );
  },
);
